import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";

const UTILITY_START = 295;
const UTILITY_RATE = 0.08;
const SOLAR_START = 244.47;
const SOLAR_RATE = 0.0299;
const ORANGE = "#C2410C";
const GREEN = "#0F382C";

const usd = (n) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });

const ROWS = (() => {
  let cumulative = 0;
  return Array.from({ length: 10 }, (_, i) => {
    const year = i + 1;
    const utility = UTILITY_START * Math.pow(1 + UTILITY_RATE, i);
    const solar = SOLAR_START * Math.pow(1 + SOLAR_RATE, i);
    const diff = utility - solar;
    cumulative += diff * 12;
    return { year, utility, solar, diff, cumulative };
  });
})();

const TOTAL_UTILITY = ROWS.reduce((s, r) => s + r.utility * 12, 0);
const TOTAL_SOLAR = ROWS.reduce((s, r) => s + r.solar * 12, 0);

const INPUTS = [
  { id: "utility-start", label: "Starting average monthly utility cost", value: "$295", note: "Customer's previous average" },
  { id: "utility-rate", label: "Assumed annual utility increase", value: "8%", note: "Assumption for this scenario — not a forecast", warn: true },
  { id: "solar-start", label: "Starting monthly solar agreement payment", value: "$244.47", note: "Customer's agreement" },
  { id: "solar-rate", label: "Annual solar agreement escalator", value: "2.99%", note: "Customer's stated agreement term" },
];

const TOTALS = [
  { id: "utility", label: "Projected utility payments over 10 years", value: TOTAL_UTILITY },
  { id: "solar", label: "Projected solar agreement payments over 10 years", value: TOTAL_SOLAR },
  { id: "difference", label: "Projected payment difference before remaining utility charges", value: TOTAL_UTILITY - TOTAL_SOLAR, accent: true },
];

function Inputs() {
  return (
    <dl className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
      {INPUTS.map((it) => (
        <div key={it.id} data-testid={`ten-year-input-${it.id}`} className={`rounded-2xl p-4 border ${it.warn ? "bg-[#FFF7ED] border-[#FDBA74]" : "bg-white border-[#E7DFCF]"}`}>
          <dt className="text-xs text-slate-600">{it.label}</dt>
          <dd className="mt-1 font-serif-display text-2xl text-[#0F382C]">{it.value}</dd>
          <dd className={`mt-1 text-xs font-semibold ${it.warn ? "text-[#9A3412]" : "text-slate-500"}`}>{it.note}</dd>
        </div>
      ))}
    </dl>
  );
}

function ProjectionTable() {
  return (
    <div className="overflow-x-auto rounded-2xl border border-[#E7DFCF] bg-white" data-testid="ten-year-table">
      <table className="w-full min-w-[640px] text-sm text-right">
        <caption className="sr-only">10-year illustrative cost comparison</caption>
        <thead className="bg-[#0F382C] text-[#FEF3C7]">
          <tr>
            <th scope="col" className="p-3 text-left">Year</th>
            <th scope="col" className="p-3">Projected average monthly utility cost</th>
            <th scope="col" className="p-3">Projected monthly solar payment</th>
            <th scope="col" className="p-3">Monthly payment difference</th>
            <th scope="col" className="p-3">Cumulative payment difference</th>
          </tr>
        </thead>
        <tbody>
          {ROWS.map((r) => (
            <tr key={r.year} className="border-t border-[#E7DFCF] even:bg-[#FDFBF7]" data-testid={`ten-year-row-${r.year}`}>
              <th scope="row" className="p-3 text-left font-semibold text-[#0F382C]">{r.year}</th>
              <td className="p-3 text-[#9A3412]">{usd(r.utility)}</td>
              <td className="p-3 text-[#0F382C]">{usd(r.solar)}</td>
              <td className="p-3 text-slate-700">{usd(r.diff)}</td>
              <td className="p-3 font-semibold text-[#0F382C]">{usd(r.cumulative)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProjectionChart() {
  return (
    <div className="rounded-2xl border border-[#E7DFCF] bg-white p-4 md:p-6" data-testid="ten-year-chart" role="img" aria-label="Line chart of projected monthly utility cost rising from $295 to $589.71 and projected monthly solar payment rising from $244.47 to $318.70 over years 1 to 10">
      <h4 className="font-serif-display text-lg text-[#0F382C]">Projected monthly cost, Years 1–10</h4>
      <div className="mt-4 h-72">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={ROWS} margin={{ top: 8, right: 16, left: 8, bottom: 20 }}>
            <CartesianGrid stroke="#E7DFCF" strokeDasharray="3 3" />
            <XAxis dataKey="year" tick={{ fill: "#334155", fontSize: 12 }} label={{ value: "Year", position: "insideBottom", offset: -12, fill: "#334155", fontSize: 12 }} />
            <YAxis tick={{ fill: "#334155", fontSize: 12 }} tickFormatter={(v) => `$${v}`} width={56} label={{ value: "Monthly cost ($)", angle: -90, position: "insideLeft", offset: 0, fill: "#334155", fontSize: 12, dy: 50 }} />
            <Tooltip formatter={(v) => usd(v)} labelFormatter={(y) => `Year ${y}`} />
            <Legend verticalAlign="top" wrapperStyle={{ fontSize: 12, lineHeight: "18px", paddingBottom: 16 }} />
            <Line type="monotone" dataKey="utility" name="Projected utility cost (8% assumed)" stroke={ORANGE} strokeWidth={3} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="solar" name="Solar agreement payment (2.99%)" stroke={GREEN} strokeWidth={3} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Totals() {
  return (
    <dl className="grid md:grid-cols-3 gap-3">
      {TOTALS.map((t) => (
        <div key={t.id} data-testid={`ten-year-total-${t.id}`} className={`rounded-2xl p-5 ${t.accent ? "bg-[#0F382C] text-[#FEF3C7]" : "bg-white border border-[#E7DFCF] text-[#0F382C]"}`}>
          <dt className={`text-sm ${t.accent ? "text-[#FCD34D]" : "text-slate-600"}`}>{t.label}</dt>
          <dd className="mt-2 font-serif-display text-3xl font-semibold">{usd(t.value)}</dd>
        </div>
      ))}
    </dl>
  );
}

export default function TenYearComparison() {
  return (
    <div className="mt-14" data-testid="ten-year-comparison">
      <span className="inline-block rounded-full bg-[#FFF7ED] border border-[#FDBA74] px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#9A3412]">
        Projected scenario — not actual customer savings
      </span>
      <h3 className="mt-3 font-serif-display text-2xl md:text-4xl font-semibold text-[#0F382C]">10-Year Illustrative Cost Comparison</h3>
      <p className="mt-3 max-w-3xl text-slate-600">
        The 8% annual utility increase is an assumption used only for this illustration. The 2.99% figure is the customer's
        stated annual solar agreement escalator.
      </p>
      <Inputs />
      <div className="mt-6 grid gap-5">
        <ProjectionChart />
        <ProjectionTable />
        <Totals />
      </div>
      <p className="mt-5 rounded-2xl bg-white/70 border border-[#E7DFCF] p-5 text-sm text-slate-700 leading-relaxed" data-testid="ten-year-disclosure">
        Illustrative projection using an assumed 8% annual utility increase and the customer's stated 2.99% annual solar
        agreement escalator. Future utility increases are not guaranteed. Solar figures exclude remaining utility charges, so
        the payment difference is not verified net savings. Actual results depend on usage, production, utility rates, and
        agreement terms.
      </p>
    </div>
  );
}
